import { HttpErrorResponse } from '@angular/common/http';
import { AppVersion } from '@application/config';
import { ERegistrationType } from './profile.model';

/**
 * The two OwnerSettings writes that complete an academy from admin scope (a
 * platform team): which app it runs on, and where its own subscription with
 * the platform lives.
 *
 * What a visitor converted into a customer owes (`tenantSetupOnConvert`): the
 * platform subscription, and the app the visitor already has (the new one
 * when it has none).
 *
 * Both writes are idempotent, so repeating them is harmless.
 */
export type TTenantOwnerSetup = {
  /** `PersonId` of the owner: in admin scope it is the tenant. */
  tenantId: string;
  /**
   * The app to write. `current` keeps the one the tenant already has, read
   * from its OwnerSettings right before the write (the new app when it has
   * none).
   */
  appVersion: AppVersion | 'current';
  /** Whether the platform subscription is written too. */
  platformSubscription: boolean;
  /** What made the write owed; reported with a failure, to tell the flows apart. */
  origin: 'create' | 'convert';
};

export function ownerAppVersionOnCreate(input: {
  isAdminScope: boolean;
  registrationType: ERegistrationType | null | undefined;
  pickedAppVersion: AppVersion | null | undefined;
}): AppVersion | null {
  if (!input.isAdminScope) return null;

  if (input.registrationType === ERegistrationType.Customer) {
    return input.pickedAppVersion ?? AppVersion.New;
  }

  if (input.registrationType === ERegistrationType.Visitor) {
    return AppVersion.New;
  }

  return null;
}

/** What converting a visitor into a customer owes (admin scope only). */
export function tenantSetupOnConvert(
  tenantId: string
): TTenantOwnerSetup {
  return {
    tenantId,
    appVersion: 'current',
    platformSubscription: true,
    origin: 'convert',
  };
}

/**
 * Pause before the single automatic retry of both writes: long enough for a
 * transient 5xx or a dropped connection to clear, short enough that the user
 * is still on the academy they just saved when the alert shows.
 */
export const TENANT_OWNER_SETUP_RETRY_DELAY_MS = 2000;

/**
 * The platform subscription of an academy is always on the new app, whatever
 * app the academy itself runs on: there is no contract to point to on legacy.
 */
export const NEW_TENANT_PLATFORM_SUBSCRIPTION_VERSION = AppVersion.New;

/**
 * What a setup failure means, from the status of the write that failed. Sent
 * to Sentry as a tag, so the issue can be mapped by cause:
 * - `forbidden` (403): the session token is not a platform team's — a flow
 *   configuration error;
 * - `invalid` (400): a bad parameter or a platform tenant as `tenantId` — a
 *   programming error;
 * - `transient` (5xx, timeout, network): the backend or the connection.
 */
export type TTenantOwnerSetupFailure = 'forbidden' | 'invalid' | 'transient';

export function tenantOwnerSetupFailure(
  error: HttpErrorResponse | Error | null | undefined
): TTenantOwnerSetupFailure {
  const status = (error as HttpErrorResponse | null)?.status;
  if (status === 403) return 'forbidden';
  if (status === 400) return 'invalid';
  return 'transient';
}

/**
 * Whether creating a person of this type in admin scope makes an academy whose
 * app is picked on screen. Only a customer: a visitor always starts on the
 * new app (`ownerAppVersionOnCreate`), so it has no picker to fill.
 */
export function createsTenant(input: {
  isAdminScope: boolean;
  registrationType: ERegistrationType | null | undefined;
}): boolean {
  return (
    input.isAdminScope &&
    input.registrationType === ERegistrationType.Customer
  );
}

const EMPTY_GUID = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;

/**
 * The OwnerSettings writes accept any id and create settings for it, so only a
 * real `PersonId` may go out. The empty GUID is an error on the backend, not
 * one of its 400s.
 */
export function isUsableTenantId(
  tenantId: string | null | undefined
): tenantId is string {
  return !!tenantId && !EMPTY_GUID.test(tenantId);
}
